import { memo, useContext, useEffect, useState } from 'react';

import { ChevronDown, Paperclip } from 'lucide-react';
import Path from 'path-to-regexp';
import { Link } from 'react-router-dom';

import { Badge } from '@/shadcn/badge';
import { cn } from '@/shadcn/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import { TraceInspectorContext } from '@/components/traceInspector/traceInspector.context';
import { AnswerCard } from '@/components/ui/answerCard/answerCard';
import { AvatarComponent } from '@/components/ui/avatar/avatar';
import { RichEditor } from '@/components/ui/richEditor';
import { SearchContext } from '@/components/ui/search/searchContext';
import type { ColumnWidth } from '@/components/ui/table/columnWidth';
import type { TextView } from '@/components/ui/widget/views/text/text.view.type';
import type { WidgetData } from '@/components/ui/widget/widget.type';
import { ROUTE_INSPECT_TRACE_LINK } from '@/constant/routes';
import {
  checkCommentExists,
  formatDateWithoutSeconds,
  isJSON,
  pluralize
} from '@/utils';
import { getByPath } from '@/utils/widgets';
import type { CommentView } from './comment.view.type';
import { PATHS } from './constants.comment';

type Props = {
  view: CommentView;
  data: WidgetData;
  index: number;
};

export const CommentViewImpl = memo(({ view, data, index }: Props) => {
  const traceInspectorContext = useContext(TraceInspectorContext);
  const { traceId, currentHash } = traceInspectorContext || {};
  const { state: searchState } = useContext(SearchContext) || {};
  const {
    namePath = PATHS.NAME,
    avatarPath = PATHS.AVATAR,
    groupPath = PATHS.GROUP,
    datePath = PATHS.DATE,
    actionPath = PATHS.ACTION,
    commentPath = PATHS.COMMENT,
    linkHashPath = PATHS.HASH,
    heightPath = PATHS.HEIGHT,
    filesPath = PATHS.FILES,
    answersPath = PATHS.ANSWERS,
    path
  } = view;

  const name = getByPath(data, namePath);
  const avatar = getByPath(data, avatarPath);
  const group = getByPath(data, groupPath);
  const date = getByPath(data, datePath);
  const actionName = getByPath(data, actionPath);
  const hash = getByPath(data, linkHashPath);
  const linkHeight = getByPath(data, heightPath);
  const files = getByPath(data, filesPath);
  const commentPathValue = commentPath || path;
  const comment = getByPath(data, commentPathValue);
  const answers = getByPath(data, answersPath);

  const isCommentWithSubject = () => {
    const commentArray = isJSON(comment) ? JSON.parse(comment) : [];
    return commentArray.some(c => c?.type === 'subject');
  };
  const [showAnswers, setShowAnswers] = useState(true);

  useEffect(() => {
    const hasMatchingAnswer = searchState?.string
      ? answers?.some(answer =>
          answer.data.answer.includes(searchState?.string)
        )
      : false;
    setShowAnswers(hasMatchingAnswer);
  }, [searchState?.string, answers]);

  const isCurrentLink = currentHash === hash;

  if (!(data && checkCommentExists(data[commentPath]))) {
    return null;
  }

  return (
    <section className="flex pb-2 text-card-foreground">
      {index !== undefined && (
        <div className="w-8 px-0.5 py-1 text-center text-sm">n°{index + 1}</div>
      )}
      <AvatarComponent name={name} source={avatar} />
      <div className="mx-[13px] min-h-[70px] w-full">
        <ul>
          {data?.user && (
            <li className="flex flex-row flex-wrap items-center">
              <div className="mr-2 font-semibold text-md">{name}</div>
              {group && (
                <div className="border-input border-l pl-2.5 text-card-foreground/80 text-xs">
                  {group}
                </div>
              )}
            </li>
          )}
          {date && (
            <li>
              {traceId && hash ? (
                <div className="flex flex-row items-center gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Link
                          aria-disabled={isCurrentLink}
                          aria-label={`Link to the ${actionName} action.`}
                          className={cn(
                            isCurrentLink &&
                              'pointer-events-none cursor-not-allowed'
                          )}
                          to={Path.compile(ROUTE_INSPECT_TRACE_LINK)({
                            id: traceId,
                            linkid: hash
                          })}
                        >
                          <Badge className="h-5 hover:border-primary-deep/40">
                            {linkHeight} - {actionName}
                          </Badge>
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent>
                        {isCurrentLink ? (
                          <p>
                            This link is disabled because you are already on the
                            action.
                          </p>
                        ) : (
                          <p>
                            Click to open the{' '}
                            <b className="font-bold">{actionName}</b> action.
                          </p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <span className="py-0.5 text-[10px] text-muted-foreground">
                    Commented on {formatDateWithoutSeconds(date)}
                  </span>
                </div>
              ) : (
                <div>
                  <span className="py-0.5 text-[10px] text-muted-foreground">
                    Commented on {formatDateWithoutSeconds(date)}
                  </span>
                </div>
              )}
            </li>
          )}
          <li>
            <div>
              <RichEditor
                readOnly
                searchValue={searchState?.string}
                value={comment}
                withSubject={isCommentWithSubject()}
              />
            </div>
          </li>
          {files?.length > 0 && traceId && hash && (
            <Link
              aria-disabled={isCurrentLink}
              aria-label={`Link to the ${actionName} action.`}
              className={cn(
                'mt-2 flex flex-row items-center gap-2 text-muted-foreground text-xs hover:text-primary-deep hover:underline',
                isCurrentLink && 'cursor-not-allowed'
              )}
              to={Path.compile(ROUTE_INSPECT_TRACE_LINK)({
                id: traceId,
                linkid: hash
              })}
            >
              <Paperclip size={14} />
              <i>{pluralize(data?.files?.length, 'Attachment')}</i>
            </Link>
          )}
          {files?.length > 0 && !(traceId && hash) && (
            <div className="mt-2 flex flex-row items-center gap-2 text-muted-foreground text-xs">
              <Paperclip size={14} />
              <i>{pluralize(data?.files?.length, 'Attachment')}</i>
            </div>
          )}
          {answers?.length > 0 && (
            <button
              className="flex items-center font-normal text-muted-foreground text-xs underline"
              onClick={() => setShowAnswers(!showAnswers)}
              type="button"
            >
              {pluralize(answers?.length, 'Answer')}
              <div
                className={cn(
                  'ml-1 transition-transform',
                  showAnswers && 'rotate-180'
                )}
              >
                <ChevronDown size={12} />
              </div>
            </button>
          )}
          {showAnswers &&
            answers?.length > 0 &&
            answers
              ?.sort((a, b) => Date.parse(b.date) - Date.parse(a.date))
              .map((answer, i) => (
                <AnswerCard
                  data={answer}
                  key={i}
                  searchValue={searchState?.string}
                />
              ))}
        </ul>
      </div>
    </section>
  );
});

/** Sorts defaults to text on `datePath` (text is ok assuming ISO dates) */
const getSortConfig = (view: CommentView): TextView => ({
  type: 'text',
  path: view.datePath || PATHS.DATE
});

/** Filters defaults to text search on comment content */
const getFilterConfig = (view: CommentView): TextView => ({
  type: 'text',
  path: view.commentPath || PATHS.COMMENT
});

/** Gets the default width which is `'large'` */
const getDefaultWidth = (): ColumnWidth => 'large';

/** Stringifies data at commentPath */
const getStringifyFunction = (view: CommentView) => {
  const { commentPath = PATHS.COMMENT } = view;
  return (data: WidgetData): unknown => getByPath(data, commentPath) || '';
};

export const commentViewProvider = {
  component: CommentViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
